import React, { useState } from 'react'
import useSWR from 'swr'
import { Link, useNavigate } from 'react-router-dom'
import { fetcher } from '../utils/api'
import PageHeader from '../components/admin/PageHeader'
import MetricCard from '../components/admin/MetricCard'
import StatCard from '../components/admin/StatCard'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Skeleton } from '../components/ui/skeleton'
import { 
  Users, Trophy, Calendar, DollarSign, TrendingUp, TrendingDown, 
  MessageSquare, Activity, Target, Zap, RefreshCw, Sparkles,
  ArrowRight, Clock, AlertCircle, ChevronRight, 
  Filter, BarChart3, Flame, AlertTriangle, Headphones, 
  CreditCard, UserMinus, Shield, Bell
} from 'lucide-react'
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area
} from 'recharts'

const CHART_TOOLTIP_STYLE = {
  backgroundColor: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  color: '#111827',
  fontSize: '12px',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
}

const SPORT_COLORS = ['#F97316', '#8B5CF6', '#10B981', '#3B82F6', '#EC4899', '#F59E0B']
const ACQ_COLORS = ['#6366F1', '#EF4444', '#374151', '#10B981']


export default function Dashboard() {
  const [period, setPeriod] = useState(30)
  const navigate = useNavigate()
  
  const { data: metrics, isLoading, mutate: refreshMetrics } = useSWR(
    `/api/admin/metrics/comprehensive?days=${period}`,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000, refreshInterval: 300000 }
  )
  
  const { data: recentUsers } = useSWR('/api/admin/users?page=1&limit=5', fetcher, { revalidateOnFocus: false })
  const { data: recentTeams } = useSWR('/api/admin/teams?page=1&limit=5', fetcher, { revalidateOnFocus: false })

  const handleRefresh = async () => {
    try {
      await fetch('/api/admin/metrics/refresh', { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
    } catch (e) { /* ignore */ }
    refreshMetrics()
  }

  if (isLoading && !metrics) return <DashboardSkeleton />

  if (!metrics) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Failed to load metrics</p>
        <Button onClick={handleRefresh} className="mt-4">Retry</Button>
      </div>
    )
  }

  const { engagement, teams, events, communication, revenue, growth, insights, alerts, support } = metrics

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-500" />
            Dashboard
          </span>
        }
        description={`Last updated ${metrics.generated_at ? new Date(metrics.generated_at).toLocaleTimeString() : 'just now'} • Cached 5 min`}
      >
        <button
          onClick={handleRefresh}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Force refresh (clears cache)"
        >
          <RefreshCw className={`w-5 h-5 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {[7, 14, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setPeriod(d)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                period === d 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </PageHeader>

      {/* Alerts Bar */}
      {alerts && (alerts.premium_at_risk > 0 || alerts.at_risk_users > 5 || (support && support.needs_attention > 0)) && (
        <AlertsBar alerts={alerts} support={support} navigate={navigate} />
      )}

      {/* Hero Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          icon={Users}
          value={growth.total_users}
          label="Total Users"
          change={growth.mom_growth}
          color="indigo"
        />
        <MetricCard
          icon={DollarSign}
          value={`$${revenue.mrr?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
          label="MRR"
          sublabel={revenue.apple_mrr > 0 ? `$${revenue.stripe_mrr?.toFixed(2)} Stripe · $${revenue.apple_mrr?.toFixed(2)} Apple` : `ARR: $${revenue.arr?.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`}
          color="emerald"
        />
        <MetricCard
          icon={Activity}
          value={engagement.mau}
          label="Monthly Active"
          change={engagement.mau_change}
          sublabel={`${engagement.dau} today · ${engagement.wau} this week`}
          color="blue"
        />
        <MetricCard
          icon={Headphones}
          value={support?.needs_attention || 0}
          label="Open Tickets"
          sublabel={support?.unread_messages ? `${support.unread_messages} unread` : undefined}
          color={support?.needs_attention > 0 ? 'amber' : 'gray'}
          onClick={() => navigate('/support')}
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'View Users', icon: Users, bgColor: 'bg-indigo-100', iconColor: 'text-indigo-600', path: '/users' },
          { label: 'View Teams', icon: Trophy, bgColor: 'bg-orange-100', iconColor: 'text-orange-600', path: '/teams' },
          { label: 'Support', icon: Headphones, bgColor: 'bg-purple-100', iconColor: 'text-purple-600', path: '/support', badge: support?.needs_attention },
          { label: 'Payments', icon: DollarSign, bgColor: 'bg-emerald-100', iconColor: 'text-emerald-600', path: '/payments' },
        ].map((action) => (
          <button
            key={action.label}
            onClick={() => navigate(action.path)}
            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-all group relative"
          >
            <div className={`w-10 h-10 ${action.bgColor} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
              <action.icon className={`w-5 h-5 ${action.iconColor}`} />
            </div>
            <span className="font-medium text-gray-700">{action.label}</span>
            {action.badge > 0 && (
              <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {action.badge}
              </span>
            )}
            <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform" />
          </button>
        ))}
      </div>

      {/* Revenue & Subscriptions */}
      <div className="space-y-4">
        <SectionHeader icon={DollarSign} color="emerald" title="Revenue & Subscriptions" />
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard title="MRR" value={`$${revenue.mrr?.toFixed(2) || 0}`} icon={DollarSign} color="emerald" />
          <StatCard title="This Month" value={`$${revenue.revenue_this_month?.toFixed(2) || 0}`} icon={TrendingUp} color="teal" />
          <StatCard title="Collection Rate" value={revenue.collection_rate || 0} suffix="%" icon={Target} color="blue" />
          <StatCard title="ARPU" value={`$${revenue.arpu || 0}`} icon={Users} color="purple" />
          <StatCard title="Pending" value={`$${revenue.pending_amount || 0}`} icon={Clock} color="amber" />
        </div>

        {revenue.apple_mrr > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Revenue Sources</h4>
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-indigo-500" />
                <span className="text-sm text-gray-600">Stripe</span>
                <span className="text-sm font-semibold text-gray-900">${revenue.stripe_mrr?.toFixed(2)}/mo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-sm text-gray-600">Apple IAP</span>
                <span className="text-sm font-semibold text-gray-900">${revenue.apple_mrr?.toFixed(2)}/mo</span>
                <span className="text-xs text-gray-400">({revenue.apple_subs} sub{revenue.apple_subs !== 1 ? 's' : ''})</span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Revenue Over Time">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue.revenue_by_day}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v) => v?.slice(5) || ''} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [`$${v}`, 'Revenue']} />
                <Area type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2} fill="url(#revenueGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <SubscriptionStatusCard subscriptions={revenue.stripe_subscriptions} products={revenue.stripe_products} />
        </div>
      </div>

      {/* Users & Growth */}
      <div className="space-y-4">
        <SectionHeader icon={Zap} color="pink" title="Users & Growth" />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard title="Total Users" value={growth.total_users} icon={Users} color="indigo" />
          <StatCard title="WoW Growth" value={growth.wow_growth} suffix="%" icon={TrendingUp} color={growth.wow_growth >= 0 ? 'emerald' : 'rose'} />
          <StatCard title="Conversion" value={growth.conversion_rate} suffix="%" icon={Target} color="purple" />
          <StatCard title="Retention" value={growth.user_retention_rate} suffix="%" icon={Shield} color="cyan" />
          <StatCard title="Churn" value={growth.churn_rate} suffix="%" icon={UserMinus} color={growth.churn_rate > 5 ? 'rose' : 'emerald'} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Daily Active Users">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={engagement.dau_by_day}>
                <defs>
                  <linearGradient id="dauGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v) => v?.slice(5) || ''} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [v, 'Active Users']} />
                <Area type="monotone" dataKey="value" stroke="#6366F1" strokeWidth={2} fill="url(#dauGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="User Signups">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={growth.signups_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v) => v?.slice(5) || ''} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [v, 'Signups']} />
                <Bar dataKey="value" fill="#EC4899" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {/* Teams & Activity */}
      <div className="space-y-4">
        <SectionHeader icon={Trophy} color="orange" title="Teams & Activity" />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard title="Total Teams" value={teams.total} icon={Trophy} color="orange" />
          <StatCard title="Active Teams" value={teams.active_teams} icon={Activity} color="emerald" />
          <StatCard title="Avg Size" value={teams.avg_team_size?.toFixed(1) || 0} icon={Users} color="blue" />
          <StatCard title="Events" value={events.total} icon={Calendar} color="purple" />
          <StatCard 
            title="Messages/Posts" 
            value={`${communication.total_messages?.toLocaleString()} / ${communication.total_posts?.toLocaleString()}`} 
            icon={MessageSquare} 
            color="cyan" 
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Teams Created">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={teams.teams_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v) => v?.slice(5) || ''} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [v, 'Teams']} />
                <Bar dataKey="value" fill="#F97316" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Events Created">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={events.events_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v) => v?.slice(5) || ''} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [v, 'Events']} />
                <Bar dataKey="value" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Recent Users</h3>
            <Link to="/users" className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-3">
            {(recentUsers?.users || recentUsers || []).slice(0, 5).map((user) => (
              <Link
                key={user.user_id}
                to={`/users/${user.user_id}`}
                className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
                  ) : (
                    <span className="text-indigo-600 font-medium text-sm">{user.name?.charAt(0)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  user.subscription_plan === 'premium' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {user.subscription_plan || 'free'}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Recent Teams</h3>
            <Link to="/teams" className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-3">
            {(recentTeams?.teams || recentTeams || []).slice(0, 5).map((team) => (
              <Link
                key={team.team_id}
                to={`/teams/${team.team_id}`}
                className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Trophy className="w-4 h-4 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{team.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{team.sport} • {team.member_count} members</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Insights */}
      {insights && (
        <div className="space-y-4">
          <SectionHeader icon={BarChart3} color="violet" title="Insights" />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Conversion Funnel */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Filter className="w-4 h-4 text-violet-500" />
                Conversion Funnel
              </h3>
              <div className="space-y-3">
                {[
                  { label: 'Signups', value: insights.conversion_funnel?.signups || 0, color: 'bg-violet-500', width: '100%' },
                  { label: 'Joined Team', value: insights.conversion_funnel?.joined_team || 0, color: 'bg-indigo-500', 
                    width: insights.conversion_funnel?.signups ? `${(insights.conversion_funnel.joined_team / insights.conversion_funnel.signups * 100)}%` : '0%',
                    rate: insights.conversion_funnel?.signup_to_team_rate },
                  { label: 'Active Users', value: insights.conversion_funnel?.active_users || 0, color: 'bg-blue-500',
                    width: insights.conversion_funnel?.signups ? `${(insights.conversion_funnel.active_users / insights.conversion_funnel.signups * 100)}%` : '0%',
                    rate: insights.conversion_funnel?.team_to_active_rate },
                  { label: 'Premium', value: insights.conversion_funnel?.premium_conversions || 0, color: 'bg-emerald-500',
                    width: insights.conversion_funnel?.signups ? `${Math.max((insights.conversion_funnel.premium_conversions / insights.conversion_funnel.signups * 100), 5)}%` : '5%',
                    rate: insights.conversion_funnel?.active_to_premium_rate },
                ].map((step, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-gray-700">{step.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{step.value}</span>
                        {step.rate !== undefined && (
                          <span className="text-xs text-gray-500">({step.rate}%)</span>
                        )}
                      </div>
                    </div>
                    <div className="h-6 bg-gray-100 rounded-lg overflow-hidden">
                      <div className={`h-full ${step.color} rounded-lg transition-all duration-500`} style={{ width: step.width }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Teams by Sport */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-orange-500" />
                Teams by Sport
              </h3>
              {insights.teams_by_sport && insights.teams_by_sport.length > 0 ? (
                <div className="flex items-center gap-6">
                  <div className="h-40 w-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={insights.teams_by_sport} cx="50%" cy="50%" innerRadius={40} outerRadius={60} dataKey="value" nameKey="name">
                          {insights.teams_by_sport.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={SPORT_COLORS[index % SPORT_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(value, name) => [value, name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-2">
                    {insights.teams_by_sport.slice(0, 5).map((sport, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SPORT_COLORS[idx % SPORT_COLORS.length] }} />
                          <span className="text-sm font-medium text-gray-700">{sport.name}</span>
                        </div>
                        <span className="font-bold text-gray-900">{sport.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">No team data available</div>
              )}
            </div>

            {/* Top Active Teams */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Flame className="w-4 h-4 text-red-500" />
                Most Active Teams
              </h3>
              {insights.top_active_teams && insights.top_active_teams.length > 0 ? (
                <div className="space-y-3">
                  {insights.top_active_teams.map((team, idx) => (
                    <Link 
                      key={team.team_id}
                      to={`/teams/${team.team_id}`}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
                        idx === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500' :
                        idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                        idx === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700' :
                        'bg-gray-300'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{team.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{team.sport || 'Team'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">{team.activity_count}</p>
                        <p className="text-xs text-gray-500">activities</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">No activity data yet</div>
              )}
            </div>

            {/* Acquisition & Plans */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                Acquisition & Plans
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Signup Source</p>
                  {insights.acquisition_sources?.filter(s => s.value > 0).map((source, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ACQ_COLORS[idx % ACQ_COLORS.length] }} />
                        <span className="text-sm text-gray-700">{source.name}</span>
                      </div>
                      <span className="font-semibold text-gray-900 text-sm">{source.value}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Plan Split</p>
                  <div className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                      <span className="text-sm text-gray-700">Premium</span>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm">{growth.premium_users}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                      <span className="text-sm text-gray-700">Free</span>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm">{growth.free_users}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Invite Accept</span>
                      <span className="text-sm font-semibold text-gray-900">{communication.invites?.acceptance_rate || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


// ── Helper Components ─────────────────────────────────────────────────

function SectionHeader({ icon: Icon, color, title }) {
  const bgColors = {
    emerald: 'bg-emerald-100', pink: 'bg-pink-100', orange: 'bg-orange-100',
    violet: 'bg-violet-100', blue: 'bg-blue-100', indigo: 'bg-indigo-100',
  }
  const textColors = {
    emerald: 'text-emerald-600', pink: 'text-pink-600', orange: 'text-orange-600',
    violet: 'text-violet-600', blue: 'text-blue-600', indigo: 'text-indigo-600',
  }
  return (
    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
      <div className={`w-8 h-8 ${bgColors[color] || 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${textColors[color] || 'text-gray-600'}`} />
      </div>
      {title}
    </h2>
  )
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
      <h3 className="font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="h-48">{children}</div>
    </div>
  )
}

function AlertsBar({ alerts, support, navigate }) {
  const items = []
  if (alerts?.premium_at_risk > 0) {
    items.push({ icon: AlertTriangle, color: 'text-red-600 bg-red-50', text: `${alerts.premium_at_risk} premium user${alerts.premium_at_risk > 1 ? 's' : ''} inactive 7+ days`, action: () => navigate('/users?filter=at_risk') })
  }
  if (alerts?.at_risk_users > 5) {
    items.push({ icon: UserMinus, color: 'text-amber-600 bg-amber-50', text: `${alerts.at_risk_users} users inactive 14+ days` })
  }
  if (alerts?.signup_dropoffs > 10) {
    items.push({ icon: Bell, color: 'text-orange-600 bg-orange-50', text: `${alerts.signup_dropoffs} signups never came back` })
  }
  if (support?.needs_attention > 0) {
    items.push({ icon: Headphones, color: 'text-purple-600 bg-purple-50', text: `${support.needs_attention} support ticket${support.needs_attention > 1 ? 's' : ''} need attention`, action: () => navigate('/support') })
  }
  if (alerts?.inactive_teams > 0) {
    items.push({ icon: Trophy, color: 'text-gray-600 bg-gray-50', text: `${alerts.inactive_teams} teams with no activity in 30 days` })
  }
  if (items.length === 0) return null
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-amber-500" />
        <h3 className="font-semibold text-gray-900 text-sm">Attention Needed</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((item, idx) => (
          <button key={idx} onClick={item.action} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:shadow-sm ${item.color}`}>
            <item.icon className="w-3.5 h-3.5" />
            {item.text}
            {item.action && <ChevronRight className="w-3 h-3 opacity-50" />}
          </button>
        ))}
      </div>
    </div>
  )
}

function SubscriptionStatusCard({ subscriptions, products }) {
  if (!subscriptions) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-emerald-500" />
          Stripe Subscriptions
        </h3>
        <div className="text-center py-8 text-gray-400">No Stripe data available</div>
      </div>
    )
  }
  const statusItems = [
    { label: 'Active', value: subscriptions.active, color: 'bg-emerald-500', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50' },
    { label: 'Trialing', value: subscriptions.trialing, color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50' },
    { label: 'Past Due', value: subscriptions.past_due, color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50' },
    { label: 'Canceled (30d)', value: subscriptions.canceled, color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' },
  ]
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <CreditCard className="w-4 h-4 text-emerald-500" />
        Stripe Subscriptions
      </h3>
      <div className="grid grid-cols-2 gap-3 mb-4">
        {statusItems.map((item) => (
          <div key={item.label} className={`${item.bgColor} rounded-xl p-3`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-2 h-2 rounded-full ${item.color}`} />
              <span className={`text-xs font-medium ${item.textColor}`}>{item.label}</span>
            </div>
            <p className={`text-2xl font-bold ${item.textColor}`}>{item.value || 0}</p>
          </div>
        ))}
      </div>
      {products && products.length > 0 && (
        <div className="border-t border-gray-200 pt-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">By Product</p>
          {products.map((prod, idx) => (
            <div key={idx} className="flex items-center justify-between py-1.5">
              <span className="text-sm text-gray-700 truncate flex-1">{prod.name}</span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">{prod.count} subs</span>
                <span className="font-semibold text-gray-900 text-sm">${(prod.mrr_cents / 100).toFixed(0)}/mo</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <Skeleton className="h-10 w-40" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-2xl" />)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  )
}
